/**
 * Centralized Design Token System
 * ================================
 * Single source of truth for ALL colors, shadows, glass effects, and typography.
 * Import `t` (theme tokens) anywhere — no hooks required for static values.
 *
 * Usage:
 *   import { t } from "../../theme";
 *   backgroundColor: t.glass.primary,
 *   color: t.text.primary,
 *   boxShadow: t.shadow.sm,
 */

// ─── Brand Colors ────────────────────────────────────────────
export const brand = {
  green: "#54ac34",
  greenDark: "#2d7a15",
  greenLight: "#66bb6a",
  blue: "#00AEEF",
  blueDark: "#0099D6",
  red: "#dc3545",
  redDark: "#c82333",
  redLight: "#ef5350",
  yellow: "#ffd54f",
  white: "#ffffff",
  black: "#000000",
};

// ─── Text Colors ─────────────────────────────────────────────
export const text = {
  primary: "#333333",
  secondary: "#555555",
  muted: "#777777",
  placeholder: "#A7A7A7",
  link: brand.blue,
  accent: brand.green,
  accentDark: brand.greenDark,
  inverse: "#ffffff",
  danger: brand.red,
};

// ─── Glass / Surface Backgrounds ─────────────────────────────
export const glass = {
  heavy: "rgba(255, 255, 255, 0.85)", // hover / selected tab
  primary: "rgba(255, 255, 255, 0.72)", // appbar, cards, footer, stats
  secondary: "rgba(255, 255, 255, 0.65)", // sidebar, tabs wrapper, card overlay
  tertiary: "rgba(255, 255, 255, 0.55)", // tabs root background
  light: "rgba(255, 255, 255, 0.4)", // notification items, subtle areas
  subtle: "rgba(255, 255, 255, 0.35)", // offer cards wrapper
  faint: "rgba(255, 255, 255, 0.15)", // very subtle overlay
};

// ─── Borders ─────────────────────────────────────────────────
export const border = {
  subtle: "1px solid rgba(0, 0, 0, 0.04)",
  light: "1px solid rgba(0, 0, 0, 0.06)",
  base: "1px solid rgba(0, 0, 0, 0.08)",
  medium: "1px solid rgba(0, 0, 0, 0.12)",
  // raw values for use in template literals
  colorSubtle: "rgba(0, 0, 0, 0.04)",
  colorLight: "rgba(0, 0, 0, 0.06)",
  colorBase: "rgba(0, 0, 0, 0.08)",
  colorMedium: "rgba(0, 0, 0, 0.12)",
};

// ─── Shadows ─────────────────────────────────────────────────
export const shadow = {
  none: "none",
  xs: "0 1px 2px rgba(0, 0, 0, 0.04)",
  sm: "0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)",
  md: "0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
  lg: "0 2px 12px rgba(0, 0, 0, 0.15)",
  up: "0 -1px 3px rgba(0, 0, 0, 0.04)", // footer (shadow goes up)
  brand: "0 1px 6px rgba(84, 172, 52, 0.25)", // green badge glow
  brandFocus: "0 0px 6px rgba(84, 172, 52, 0.4)", // selected brand card
  blueFocus: "0 2px 4px rgba(0, 174, 239, 0.3)", // tab indicator
};

// ─── Backdrop Filters (Glassmorphism) ────────────────────────
export const blur = {
  heavy: "saturate(180%) blur(20px)", // main glass surfaces
  medium: "saturate(180%) blur(16px)", // secondary surfaces
  light: "saturate(180%) blur(12px)", // subtle glass
  minimal: "blur(6px)", // very light blur
};

// ─── Green Accent Palette (sidebar, badges) ──────────────────
export const green = {
  bg: "rgba(84, 172, 52, 0.06)",
  bgHover: "rgba(84, 172, 52, 0.1)",
  bgSelected: "rgba(84, 172, 52, 0.12)",
  border: "rgba(84, 172, 52, 0.15)",
  borderSelected: "rgba(84, 172, 52, 0.25)",
  scrollbar: "rgba(84, 172, 52, 0.3)",
  scrollbarHover: "rgba(84, 172, 52, 0.5)",
  glow: "rgba(84, 172, 52, 0.4)",
  gradient: "linear-gradient(135deg, #54ac34 0%, #4a9b2e 100%)",
  solid: brand.green,
  dark: brand.greenDark,
};

// ─── Blue Accent Palette (tabs, links, badges) ──────────────
export const blue = {
  hover: "rgba(0, 174, 239, 0.08)",
  glow: "rgba(0, 174, 239, 0.3)",
  gradient: "linear-gradient(90deg, #00AEEF 0%, #0099D6 100%)",
  solid: brand.blue,
  dark: brand.blueDark,
};

// ─── Red Accent Palette (redeem, danger) ─────────────────────
export const red = {
  gradient: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
  glow: "rgba(220, 53, 69, 0.3)",
  solid: brand.red,
};

// ─── Typography ──────────────────────────────────────────────
export const font = {
  family:
    "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// ─── Radius ──────────────────────────────────────────────────
export const radius = {
  sm: "8px",
  md: "12px",
  lg: "20px",
  xl: "25px",
  pill: "50px",
  circle: "50%",
};

// ─── Transitions ─────────────────────────────────────────────
export const transition = {
  fast: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  base: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  smooth: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  linear: "all 0.3s linear",
};

// ─── Pre-composed Glass Styles (common combos) ──────────────
export const glassStyle = {
  /** AppBar, Footer, StatsContainer, OfferCard container */
  primary: {
    background: glass.primary,
    backdropFilter: blur.heavy,
    WebkitBackdropFilter: blur.heavy,
    border: border.base,
    boxShadow: shadow.sm,
  },
  /** Sidebar, tabsWrapper, overlays */
  secondary: {
    background: glass.secondary,
    backdropFilter: blur.heavy,
    WebkitBackdropFilter: blur.heavy,
    border: border.base,
    boxShadow: shadow.xs,
  },
  /** Tabs root, subtle panels */
  tertiary: {
    background: glass.tertiary,
    backdropFilter: blur.medium,
    WebkitBackdropFilter: blur.medium,
    borderBottom: border.light,
    boxShadow: shadow.none,
  },
};

// ─── Single default export ───────────────────────────────────
const t = {
  brand,
  text,
  glass,
  border,
  shadow,
  blur,
  green,
  blue,
  red,
  font,
  radius,
  transition,
  glassStyle,
};

export default t;
